import { useEffect, useRef } from "react";

import { Trans } from "@lingui/react/macro";
import {
  sessionQueryOptions,
  takeLastRefreshFailure,
  useSession,
  userProfileQueryOptions,
} from "@pontimed/auth-client";
import { Navigate, Outlet, createFileRoute } from "@tanstack/react-router";

import { AccountEventsListener } from "@/components/account/account-events-listener";
import { AddModals } from "@/components/account/add/add-modals";
import { DeletionPendingBanner } from "@/components/account/deletion-pending-banner";
import { useAuthModal } from "@/components/auth/auth-modal-context";
import { useCountryGateBlocking } from "@/components/country/country-gate";
import { AppShell } from "@/components/layout/app-shell";
import { useMfaEnforcer } from "@/lib/hooks/use-mfa-enforcer";
import { sidebarDataQueryOptions } from "@/lib/queries/sidebar-data";

/**
 * Global add-workspace modal search params. Live on the `_authenticated`
 * layout so they are valid on every authenticated page — the modal can be
 * triggered from workspace switchers (header dropdown + bottom-sheet) and
 * dashboard shortcuts without route navigation. See `AddModals` for the
 * conditional sheet stack.
 */
type AddTarget = "provider" | "facility" | "business";
type AddMode = "claim";
interface AuthenticatedSearch {
  add?: AddTarget;
  /** Wizard step inside the add modal (1-N). Steps 1+2 render the shared
   *  Type/Verify pre-step content via `UnclaimedSearchStep`; steps 3..N are
   *  entity-specific. Renamed from `step` to avoid collision with the
   *  `step?: string` validated on `_anon/find` — TanStack Router merges all
   *  routes' search schemas into one global type. */
  aStep?: number;
  /** Divergent claim flow triggered from step 2 (Verify) when the user picks
   *  an existing unclaimed entity from the search results. */
  aMode?: AddMode;
  /** Entity id being claimed when `aMode === "claim"`. */
  claimId?: string;
}

/**
 * Auth gate. All `/account/*`, `/dashboard`, `/messages`, `/journal` etc.
 * live underneath this layout.
 *
 * Flow when unauthenticated:
 *  1. Keep the original URL (no redirect) so deep-links survive a refresh
 *     after sign-in.
 *  2. Render the AppShell chrome with a skeleton in place of the page so the
 *     auth modal has visual context to overlay.
 *  3. Auto-open the auth modal as NON-DISMISSIBLE (X / backdrop / ESC / drag
 *     all blocked). The user can't close their way into a half-broken
 *     skeleton screen — they either complete sign-in, or use the explicit
 *     `escapeAction` button that takes them to /find.
 *
 *  After successful sign-in the session query is invalidated, status flips
 *  to "authenticated", and this layout re-renders with the real Outlet.
 *  No window navigation is needed — the URL is already correct.
 */
export const Route = createFileRoute("/_authenticated")({
  validateSearch: (search: Record<string, unknown>): AuthenticatedSearch => {
    const add = search.add;
    const aStep = search.aStep;
    const aMode = search.aMode;
    const claimId = search.claimId;
    const validAdd: AddTarget[] = ["provider", "facility", "business"];
    const validMode: AddMode[] = ["claim"];
    // Coerce string→number — search params in URLs arrive as strings on
    // initial parse; only the second hop sees the validated number.
    const stepNum =
      typeof aStep === "number"
        ? aStep
        : typeof aStep === "string"
          ? Number.parseInt(aStep, 10)
          : NaN;
    return {
      add:
        typeof add === "string" && validAdd.includes(add as AddTarget)
          ? (add as AddTarget)
          : undefined,
      aStep: Number.isFinite(stepNum) ? stepNum : undefined,
      aMode:
        typeof aMode === "string" && validMode.includes(aMode as AddMode)
          ? (aMode as AddMode)
          : undefined,
      claimId: typeof claimId === "string" ? claimId : undefined,
    };
  },
  /**
   * Kick session + userProfile + sidebar-data off in parallel with the route
   * chunk download. By the time `AuthenticatedLayout` (and any account child
   * route) mounts, all three queries read from the cache instead of
   * triggering fresh roundtrips:
   *
   *   - session   : awaited (route can't render anything until we know
   *                 whether the user is logged in)
   *   - profile   : fire-and-forget; needed by header / dashboard banners
   *   - sidebar   : fire-and-forget; needed by header + side-nav + bottom
   *                 tab bar + workspace switcher on EVERY authenticated
   *                 page — the highest-leverage prefetch of the bunch
   *
   * Anonymous visitors only pay the session round-trip; profile + sidebar
   * are skipped to avoid a pointless 401.
   */
  loader: async ({ context }) => {
    // `ensureQueryData` honors `sessionQueryOptions.retry`, so by the time we
    // reach a thrown error here the query has already retried 3x with backoff
    // (5xx, network blip, CORS during apps/api restart) — the outage is no
    // longer transient. Swallow it so the route still mounts: useSession will
    // read the same cached error state and `refetchOnWindowFocus` /
    // staleTime / manual refetch take over from there. Without this the
    // RootError UI ("Failed to fetch / Reload") replaces the entire app and
    // the user has to hard-reload.
    let session = null;
    try {
      session = await context.queryClient.ensureQueryData(sessionQueryOptions);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[auth] session loader failed transiently; mounting anyway:", error);
      }
    }
    if (session) {
      void context.queryClient.prefetchQuery(userProfileQueryOptions);
      void context.queryClient.prefetchQuery(sidebarDataQueryOptions);
    }
    return session;
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { status } = useSession();
  const { open: openAuthModal, close: closeAuthModal, isOpen: authModalOpen } = useAuthModal();
  const triggered = useRef(false);
  // First visit without a country cookie: the blocking country picker comes
  // first; the sign-in sheet opens only after a country is picked (P4).
  const countryGateBlocking = useCountryGateBlocking();

  // Watches every fetch for 403 TOTP_REQUIRED → redirects user to MFA
  // enrollment when they touch a provider/facility surface without a
  // registered 2FA factor. NIS2 + EHDS compliance for medical workspaces.
  useMfaEnforcer();

  useEffect(() => {
    if (status !== "unauthenticated") {
      // Reset trigger if the user logs out and back in within the same mount.
      triggered.current = false;
      // Auto-close any auth modal that was opened during a transient outage:
      // the session retry succeeded in the background, so the modal is now
      // a stale overlay on top of an already-authenticated page.
      //
      // Safe because `authModalOpen` means "the modal is asking to SIGN IN",
      // not "the modal is mounted": once auth succeeds the context flips it
      // to false, so the post-signup profile-completion step (which runs
      // while status is already "authenticated") is never torn down here.
      // See the `isOpen` note in auth-modal-context.tsx.
      if (status === "authenticated" && authModalOpen) {
        closeAuthModal();
      }
      return;
    }
    if (triggered.current) return;
    if (countryGateBlocking) return;
    triggered.current = true;
    const path =
      typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined;
    // If the server ended the session on purpose (absolute cap / idle
    // timeout), say so — the modal otherwise appears mid-task with no reason.
    const failure = takeLastRefreshFailure();
    const intent =
      failure?.code === "SESSION_EXPIRED"
        ? failure.reason === "idle_timeout"
          ? "session-idle"
          : "session-expired"
        : undefined;
    openAuthModal({
      returnTo: path,
      intent,
      dismissible: false,
      escapeAction: {
        label: <Trans>Continue browsing without account</Trans>,
        onClick: () => {
          if (typeof window !== "undefined") {
            window.location.replace("/find");
          }
        },
      },
    });
  }, [status, openAuthModal, closeAuthModal, authModalOpen, countryGateBlocking]);

  // Render the AppShell chrome (header + nav) immediately with a skeleton while
  // the session resolves — the same shell the unauthenticated branch shows — so
  // first paint is the app frame, not a bare full-screen spinner on a blank
  // canvas (D-A1 skeleton-first). AppShell already tolerates a not-yet-known
  // user (it's what the unauthenticated branch renders).
  if (status === "loading") {
    return (
      <AppShell>
        <AuthGatedSkeleton />
      </AppShell>
    );
  }

  if (status === "unauthenticated") {
    if (typeof window === "undefined") {
      // SSR fallback — should not happen in the SPA build but keeps types happy.
      return <Navigate to="/find" replace />;
    }
    return (
      <AppShell>
        <AuthGatedSkeleton />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <AccountEventsListener />
      {/* Pending soft-deletion warning + its Cancel action. Mounted on the
          layout (as the legacy account layout did) rather than on a page:
          during the 30-day retention window this banner is the ONLY
          self-service way to undo a scheduled deletion, so it must be
          reachable from whatever authenticated surface the user lands on.
          Renders null when there is nothing pending. */}
      <DeletionPendingBanner />
      <Outlet />
      <AddModals />
    </AppShell>
  );
}

/**
 * Neutral content placeholder shown beneath the auth modal while a guarded
 * route is gated. Matches the page-level rhythm of account pages (heading +
 * a few card rows) so the modal opens over something coherent rather than a
 * blank canvas.
 */
function AuthGatedSkeleton() {
  return (
    <div className="space-y-6" aria-hidden>
      <div className="bg-muted h-8 w-48 animate-pulse rounded-lg" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-muted h-32 animate-pulse rounded-xl" />
        <div className="bg-muted h-32 animate-pulse rounded-xl" />
        <div className="bg-muted h-32 animate-pulse rounded-xl" />
        <div className="bg-muted h-32 animate-pulse rounded-xl" />
      </div>
      <div className="bg-muted h-48 animate-pulse rounded-xl" />
    </div>
  );
}
